/**
 * Kit-of-parts rack frame (ADR 185/187): turns the structure volumes into the parts of a real
 * selective pallet rack — navy uprights + orange beams — instead of the translucent slot box.
 * Parametric: fits ANY warehouse's real dimensions (warehouse_UL.glb's racks are fixed-size
 * welded runs; only their colours are reused).
 *
 * The scan gives one drifted box per bin per level (the per-level kit maps draw the same
 * physical column up to ~0.7 m apart), so geometry is NEVER built from raw boxes. It is built
 * from the structure identity each volume carries (ADR 187/188):
 *  - volumes partition by the graph's **aisleLabel first** (ADR 188): each aisle detects its
 *    own axis and clusters its own rows, so perpendicular aisles keep their orientation and
 *    back-to-back neighbours closer than depthClusterGapM never weld into one rack;
 *  - bins collapse into **physical columns** by the bound space node's code
 *    (`…/level-1/space-065` → `065`), falling back to the address position token
 *    (`JY03-00A` → `00A`); a column's centre/width is the median of its per-level boxes —
 *    drift cancels;
 *  - each rack row is consolidated to **one front plane + one back plane** (depth cluster);
 *  - columns chunk into **bays of {@link RackFrameOptions.frameEvery} positions**; an upright
 *    frame (front + back post, tied by depth beams) stands between bays and at run ends —
 *    runs split where the clear column gap exceeds {@link RackFrameOptions.runBreakM};
 *  - per bay per LEVEL (the graph's level number, not a z-bucket): **one front + one back beam
 *    at the level's TOP**, spanning post-to-post so beams land ON the uprights. A column that
 *    lacks the level (tunnel bays) shortens the beam to the columns that have it;
 *  - depth ties at a frame use only the ADJACENT bays' level tops — a level that exists only
 *    at the far end of the rack does not ladder every frame;
 *  - **the lowest level's own BASE carries a beam too, but only when it is off the floor**
 *    (ADR 320). A level-1-on-the-floor rack keeps the original "nothing at the ground" rule —
 *    the pallet sits on the slab. A rack whose lowest bin starts metres up (Flex/Prédio 10
 *    begins at level 2, 3,0 m) rests that level on a beam like any other, and omitting it
 *    left the loads hanging in mid-air with clear sky underneath.
 *
 * The aisle axis (the one bays tile along) is auto-detected as the larger overall extent, so
 * "along the aisle" / "front-back" are well-defined. Pure (no three.js/React): works in the same
 * floor-plane shape frame as rackVolume.ts (y already negated for the `rotation-x = -π/2` group),
 * z = elevation. The layer instances one box per post/beam.
 */
import { footprintToShapePoints, type RackVolume } from "./rackVolume";

/** A vertical upright (navy), floor (z=0) → {@link top}. */
export interface RackPost { x: number; y: number; top: number; }
/** A horizontal beam (orange) at height {@link z}, from (ax,ay) to (bx,by). */
export interface RackBeam { ax: number; ay: number; bx: number; by: number; z: number; }
export interface RackFrame { posts: RackPost[]; beams: RackBeam[]; }

export interface RackFrameOptions {
  /** Override the auto-detected aisle axis. */
  aisle?: "x" | "y";
  /** Split a rack row into separate runs when the clear gap between neighbouring columns exceeds this (m). */
  runBreakM?: number;
  /** Floor plane tolerance: a beam whose height is at/below this is not drawn — it would be
   *  buried in the slab. Applies to a level's top (degenerate flat readings) and to the
   *  lowest level's base, which is exactly how "no beam under a ground-level pallet" falls
   *  out of the general rule (ADR 320). */
  floorEpsM?: number;
  /** Pallet positions (columns) per bay — an upright frame stands between bays (2 = the two-position island). */
  frameEvery?: number;
  /** Split into separate racks when the depth-centre gap between bins exceeds this (metres). */
  depthClusterGapM?: number;
}

interface Box { xmin: number; xmax: number; ymin: number; ymax: number; }
function boxOf(pts: [number, number][]): Box {
  let xmin = Infinity, xmax = -Infinity, ymin = Infinity, ymax = -Infinity;
  for (const [x, y] of pts) {
    xmin = Math.min(xmin, x); xmax = Math.max(xmax, x);
    ymin = Math.min(ymin, y); ymax = Math.max(ymax, y);
  }
  return { xmin, xmax, ymin, ymax };
}

const BUCKET_M = 0.1;
const bucket = (v: number) => Math.round(v / BUCKET_M);
const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  return (s[(s.length - 1) >> 1] + s[s.length >> 1]) / 2;
};

/** Along-aisle span + depth span + elevation + identity of one bin, in the aisle/depth frame. */
interface Item { a0: number; a1: number; d0: number; d1: number; base: number; top: number; address: string; level: number; nodeId?: string | null; deep?: boolean; }
/** A physical rack column: all of one position's bins, drift collapsed to a canonical span. */
interface Col { centre: number; halfW: number; bins: Item[]; }
/** One bay level: beam height + the along-aisle span of the columns that actually have it. */
interface BayLevel { z: number; a0: number; a1: number; }

/**
 * The physical-position identity of a bin, best source first (ADR 188):
 * the bound space node's code (`…/level-1/space-065` → `065` — the backend's grammar
 * already resolved the position, dash or not), else the address part after the dash
 * (`JY03-00A` → `00A`), else the bin stands alone (keyed by its own spot) — without a
 * dash or a binding there is no cross-level identity to collapse.
 */
const columnKey = (it: Item): string => {
  const m = it.nodeId?.match(/\/space-([^/]+)$/);
  if (m) return m[1];
  const i = it.address.indexOf("-");
  return i >= 0 ? it.address.slice(i + 1) : `${it.address}@${Math.round((it.a0 + it.a1) / 2)}`;
};

/** A beam running ALONG the aisle at depth `d`, from a0→a1. */
const aisleBeam = (aisleX: boolean, a0: number, a1: number, d: number, z: number): RackBeam =>
  aisleX ? { ax: a0, ay: d, bx: a1, by: d, z } : { ax: d, ay: a0, bx: d, by: a1, z };
/** A beam ACROSS the rack (front→back) at aisle position `p` — the frame's tie. */
const depthBeam = (aisleX: boolean, p: number, dF: number, dB: number, z: number): RackBeam =>
  aisleX ? { ax: p, ay: dF, bx: p, by: dB, z } : { ax: dF, ay: p, bx: dB, by: p, z };
const mkPost = (aisleX: boolean, p: number, d: number, top: number): RackPost =>
  aisleX ? { x: p, y: d, top } : { x: d, y: p, top };

export function buildRackFrame(volumes: RackVolume[], opts: RackFrameOptions = {}): RackFrame {
  // ADR 188: one part per graph aisle — its own axis, its own rows. Unlabeled bins
  // share one part, which is exactly the pre-188 behaviour (JY renders identically).
  const byAisle = new Map<string, RackVolume[]>();
  for (const v of volumes) {
    const k = v.aisleLabel ?? "";
    const arr = byAisle.get(k) ?? [];
    arr.push(v);
    byAisle.set(k, arr);
  }
  const posts: RackPost[] = [];
  const beams: RackBeam[] = [];
  for (const part of byAisle.values()) {
    const built = buildAislePart(part, opts);
    posts.push(...built.posts);
    beams.push(...built.beams);
  }
  return { posts, beams };
}

function buildAislePart(volumes: RackVolume[], opts: RackFrameOptions): RackFrame {
  const runBreak = opts.runBreakM ?? 1.5;
  const floorEps = opts.floorEpsM ?? 0.05;
  const frameEvery = Math.max(1, Math.floor(opts.frameEvery ?? 2));
  const depthGap = opts.depthClusterGapM ?? 2.0;

  const boxes = volumes
    .filter((v) => v.footprint && v.footprint.length >= 3)
    .map((v) => ({
      box: boxOf(footprintToShapePoints(v.footprint)),
      base: v.baseM, top: v.baseM + Math.max(0, v.heightM),
      address: v.address, level: v.level, nodeId: v.nodeId, deep: v.depthSlots === 2,
    }));
  if (!boxes.length) return { posts: [], beams: [] };

  const world = boxes.reduce<Box>((a, b) => ({
    xmin: Math.min(a.xmin, b.box.xmin), xmax: Math.max(a.xmax, b.box.xmax),
    ymin: Math.min(a.ymin, b.box.ymin), ymax: Math.max(a.ymax, b.box.ymax),
  }), { ...boxes[0].box });
  const aisleX = opts.aisle ? opts.aisle === "x" : (world.xmax - world.xmin) >= (world.ymax - world.ymin);

  const items: Item[] = boxes.map((b) => aisleX
    ? { a0: b.box.xmin, a1: b.box.xmax, d0: b.box.ymin, d1: b.box.ymax, base: b.base, top: b.top, address: b.address, level: b.level, nodeId: b.nodeId, deep: b.deep }
    : { a0: b.box.ymin, a1: b.box.ymax, d0: b.box.xmin, d1: b.box.xmax, base: b.base, top: b.top, address: b.address, level: b.level, nodeId: b.nodeId, deep: b.deep });

  // Cluster bins into rack rows by depth centre — a big depth gap means a different rack, not
  // the same one; within a row, per-level scan drift collapses to one front/back plane.
  const racks: Item[][] = [];
  let cur: Item[] = [];
  let prev: number | null = null;
  for (const it of [...items].sort((p, q) => (p.d0 + p.d1) - (q.d0 + q.d1))) {
    const c = (it.d0 + it.d1) / 2;
    if (prev !== null && c - prev > depthGap && cur.length) { racks.push(cur); cur = []; }
    cur.push(it); prev = c;
  }
  if (cur.length) racks.push(cur);

  const posts: RackPost[] = [];
  const beams: RackBeam[] = [];
  for (const rack of racks) {
    const dFront = Math.min(...rack.map((r) => r.d0));
    const dBack = Math.max(...rack.map((r) => r.d1));
    // ADR 425: a double-deep row stands a middle line of uprights and beams between
    // its front and back pallet — each pallet rests on its own beam pair.
    const dMid = rack.filter((r) => r.deep).length * 2 > rack.length ? (dFront + dBack) / 2 : null;

    // Bins → physical columns (drift-collapsed), ordered along the aisle.
    const byKey = new Map<string, Item[]>();
    for (const it of rack) {
      const k = columnKey(it);
      const arr = byKey.get(k) ?? [];
      arr.push(it);
      byKey.set(k, arr);
    }
    const cols: Col[] = [...byKey.values()].map((bins) => ({
      centre: median(bins.map((b) => (b.a0 + b.a1) / 2)),
      halfW: median(bins.map((b) => (b.a1 - b.a0) / 2)),
      bins,
    })).sort((p, q) => p.centre - q.centre);

    // Columns → runs (split at real corridors), runs → bays of frameEvery positions.
    const runs: Col[][] = [];
    let run: Col[] = [];
    for (const c of cols) {
      const last = run[run.length - 1];
      if (last && (c.centre - c.halfW) - (last.centre + last.halfW) > runBreak) { runs.push(run); run = []; }
      run.push(c);
    }
    if (run.length) runs.push(run);

    for (const rcols of runs) {
      const bays: Col[][] = [];
      for (let i = 0; i < rcols.length; i += frameEvery) bays.push(rcols.slice(i, i + frameEvery));

      // Frame positions: run start edge, the gap middle between bays, run end edge.
      const framePs: number[] = [rcols[0].centre - rcols[0].halfW];
      for (let bi = 1; bi < bays.length; bi++) {
        const left = bays[bi - 1][bays[bi - 1].length - 1];
        const right = bays[bi][0];
        framePs.push(((left.centre + left.halfW) + (right.centre - right.halfW)) / 2);
      }
      framePs.push(rcols[rcols.length - 1].centre + rcols[rcols.length - 1].halfW);

      // Per bay: one beam pair per graph LEVEL at the level's top, post-to-post when the bay's
      // edge columns have the level, shortened to the owning columns when one lacks it (tunnels).
      const bayLevels: BayLevel[][] = bays.map((bay, bi) => {
        const byLevel = new Map<number, { tops: number[]; bases: number[]; cols: Set<Col> }>();
        for (const c of bay) {
          for (const b of c.bins) {
            const e = byLevel.get(b.level) ?? { tops: [], bases: [], cols: new Set<Col>() };
            e.tops.push(b.top);
            e.bases.push(b.base);
            e.cols.add(c);
            byLevel.set(b.level, e);
          }
        }
        // Post-to-post when the bay's edge columns carry the level; shortened to the
        // columns that own it when one does not (tunnel bays keep their opening).
        const span = (has: Set<Col>): [number, number] => {
          const owning = bay.filter((c) => has.has(c));
          return [
            has.has(bay[0]) ? framePs[bi] : Math.min(...owning.map((c) => c.centre - c.halfW)),
            has.has(bay[bay.length - 1]) ? framePs[bi + 1] : Math.max(...owning.map((c) => c.centre + c.halfW)),
          ];
        };
        const out: BayLevel[] = [];
        for (const { tops, cols: has } of byLevel.values()) {
          const z = Math.max(...tops); // the beam clears the tallest reading of the level
          if (z <= floorEps) continue;
          const [a0, a1] = span(has);
          out.push({ z, a0, a1 });
        }
        // ADR 320 — the beam the level tops cannot express: every level's top is the plane
        // the level ABOVE rests on, so the lowest level's own base is the one deck with no
        // steel under it. On the floor that is right (the pallet sits on the slab); metres up
        // it is a rack drawn with its bottom load floating. Under the lowest reading of the
        // level, mirroring the max used for tops.
        const lowest = Math.min(...byLevel.keys());
        const ground = byLevel.get(lowest);
        if (ground) {
          const z = Math.min(...ground.bases);
          if (z > floorEps) {
            const [a0, a1] = span(ground.cols);
            out.push({ z, a0, a1 });
          }
        }
        return out.sort((p, q) => p.z - q.z);
      });
      for (const lvls of bayLevels) {
        for (const { z, a0, a1 } of lvls) {
          beams.push(aisleBeam(aisleX, a0, a1, dFront, z));
          beams.push(aisleBeam(aisleX, a0, a1, dBack, z));
          if (dMid != null) beams.push(aisleBeam(aisleX, a0, a1, dMid, z));
        }
      }

      // Upright frames: full-height front+back posts + depth ties at the ADJACENT bays' levels.
      for (let fi = 0; fi < framePs.length; fi++) {
        const adjacent = [bays[fi - 1], bays[fi]].filter(Boolean);
        const top = Math.max(...adjacent.flatMap((bay) => bay.flatMap((c) => c.bins.map((b) => b.top))));
        posts.push(mkPost(aisleX, framePs[fi], dFront, top));
        posts.push(mkPost(aisleX, framePs[fi], dBack, top));
        if (dMid != null) posts.push(mkPost(aisleX, framePs[fi], dMid, top));
        const tieZs = new Map<number, number>();
        for (const bi of [fi - 1, fi]) {
          for (const { z } of bayLevels[bi] ?? []) {
            if (!tieZs.has(bucket(z))) tieZs.set(bucket(z), z);
          }
        }
        for (const z of tieZs.values()) beams.push(depthBeam(aisleX, framePs[fi], dFront, dBack, z));
      }
    }
  }
  return { posts, beams };
}
